import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | pending | error

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const maxAttempts = 6;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          setState("pending");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        attempts += 1;
        if (attempts >= maxAttempts) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  const content = {
    checking: { icon: <Loader2 className="animate-spin text-sky-brand" size={64} />, title: "Confirming your payment…", text: "Hang tight — verifying with our payment processor." },
    success: { icon: <CheckCircle2 className="text-sky-brand" size={64} />, title: "Payment successful!", text: "Thank you! We've received your payment and will be in touch shortly to kick things off." },
    pending: { icon: <Loader2 className="animate-spin text-sky-brand" size={64} />, title: "Payment processing", text: "Your payment is still processing. You'll get a confirmation once it completes." },
    error: { icon: <XCircle className="text-red-400" size={64} />, title: "We couldn't confirm payment", text: "Something went wrong or the session expired. Please try again from the services page." },
  }[state];

  return (
    <div className="bg-white min-h-screen flex items-center">
      <div className="max-w-xl mx-auto px-6 text-center py-40" data-testid="checkout-success-page">
        <div className="flex justify-center">{content.icon}</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-charcoal mt-8" data-testid="checkout-status-title">{content.title}</h1>
        <p className="font-body text-gray-500 mt-4 text-lg">{content.text}</p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/" className="font-display bg-sky-brand text-charcoal hover:bg-charcoal hover:text-white transition-colors font-semibold px-8 py-4 rounded-full inline-flex items-center justify-center gap-2">
            Back home <ArrowRight size={18} />
          </Link>
          <Link to="/services" className="font-display border border-gray-200 text-charcoal hover:border-sky-brand transition-colors font-semibold px-8 py-4 rounded-full inline-flex items-center justify-center">
            View services
          </Link>
        </div>
      </div>
    </div>
  );
}
